import React, { useEffect, useContext, useState } from 'react';
import { Input, Modal, ModalBody } from '@windmill/react-ui';
import { SidebarContext } from 'context/SidebarContext';
import LabelArea from 'components/form/LabelArea';
import InputSearchDate from 'components/form/InputSearchDate';

const BillDetailsPaymentModal = ({
	isOpen,
	onClose,
	title,
	handleSubmit,
	requestAmount, // <-- payable ceiling for this bill (bill_amount, or bill_adjusted_amount once set)
	roundedOffDifference,
	outstandingData = [], // <-- vendor's unresolved debts, from check-outstanding
}) => {
	const { closeModal } = useContext(SidebarContext);
	const [utr, setUtr] = useState('');
	const [remark, setRemark] = useState('');
	const [searchstartDate, setSearchStartDate] = useState(new Date());
	const [paymentType, setPaymentType] = useState(null);
	const [selectedIds, setSelectedIds] = useState([]);
	const [totalAdjusted, setTotalAdjusted] = useState(0);
	// Tracks how much of each checked debt is actually being used (id -> contributed
	// amount) — capped at what's still needed, not the debt's full outstanding amount,
	// e.g. a ₹500 debt against a ₹1000 payable only contributes ₹500 here if that's
	// all that's owed after other selections, leaving the rest untouched for future use.
	const [selectedContributions, setSelectedContributions] = useState({});
	const [payableAmount, setPayableAmount] = useState(requestAmount);
	const [isAutoAdjusted, setIsAutoAdjusted] = useState(false);

	const [errors, setErrors] = useState({});

	useEffect(() => {
		setPayableAmount(requestAmount);
	}, [requestAmount]);

	useEffect(() => {
		if (isOpen) {
			setPaymentType(null);
			setSearchStartDate(new Date());
			setRemark('');
			setUtr('');
			setSelectedIds([]);
			setTotalAdjusted(0);
			setSelectedContributions({});
			setPayableAmount(requestAmount);
			setIsAutoAdjusted(false);
			setErrors({});
		}
	}, [isOpen, requestAmount]);

	const handleCheckboxChange = (outstandingAmount, id, isChecked) => {
		const numericAmount = Number(outstandingAmount) || 0;
		const newContributions = { ...selectedContributions };
		let newTotal;
		let newSelectedIds;

		if (isChecked) {
			const remainingBefore = Math.max(0, Number(requestAmount) - totalAdjusted);
			const contributed = Math.min(numericAmount, remainingBefore);
			newContributions[id] = contributed;
			newTotal = totalAdjusted + contributed;
			newSelectedIds = [...selectedIds, id];
		} else {
			const contributed = newContributions[id] || 0;
			delete newContributions[id];
			newTotal = totalAdjusted - contributed;
			newSelectedIds = selectedIds.filter((selectedId) => selectedId !== id);
		}

		setSelectedIds(newSelectedIds);
		setSelectedContributions(newContributions);
		setTotalAdjusted(newTotal);

		const remaining = Number(requestAmount) - newTotal;

		if (remaining > 0) {
			setPayableAmount(remaining);
			setIsAutoAdjusted(false);
		} else {
			// Fully covered by the selected debt(s) — no cash owed this round.
			setPayableAmount(0);
			setIsAutoAdjusted(true);
			setPaymentType(null);
			setRemark('');
			setUtr('');
		}
		setErrors({});
	};

	// A genuinely negative requestAmount means the vendor already owes money
	// back (consumed debt/adjustment exceeded the bill) — a real, distinct
	// state from "fully covered by adjustment." It must never be silently
	// treated as ready-to-save with nothing selected; it needs its own
	// explicit handling (outstanding recovery), not a payment submission.
	const isNegativeBill = Number(requestAmount) < 0;

	// Only "fully covered" when the payer has actually selected debt(s) that
	// cover it — not just because requestAmount itself happens to be <= 0
	// before anything was ever selected (totalAdjusted starts at 0, and
	// 0 >= a negative requestAmount would otherwise falsely read as covered).
	const isSelectionDisabled =
		totalAdjusted > 0 && totalAdjusted >= Number(requestAmount);

	// True whenever there's no actual cash to record right now — either the
	// selected debt(s) fully covered it (isAutoAdjusted), or the payer manually
	// cleared/zeroed the Payable Amount field themselves. Either way, no
	// Payment Type is needed; the outstanding-debt adjustment alone applies.
	const noCashNeeded =
		isAutoAdjusted || payableAmount === '' || Number(payableAmount) <= 0;

	const onSubmit = () => {
		const newErrors = {};
		if (isNegativeBill) {
			newErrors.amount = `This bill shows a negative payable amount (₹${requestAmount}) — the vendor appears to owe money back. This can't be recorded as a payment; it needs to be resolved through outstanding recovery first.`;
			setErrors(newErrors);
			return;
		}
		if (!searchstartDate) newErrors.date = 'Payment Date is required.';
		if (!noCashNeeded && !paymentType) newErrors.type = 'Payment Type is required.';
		if (paymentType === 'Bank Transaction' && !utr.trim()) newErrors.utr = 'UTR / Ref ID is required.';
		if (!noCashNeeded && (payableAmount === '' || Number(payableAmount) <= 0)) {
			newErrors.amount = 'A valid payment amount is required.';
		}
		// If there's genuinely nothing to record — no cash and no debt selected —
		// there's nothing for this submission to actually do.
		if (noCashNeeded && selectedIds.length === 0) {
			newErrors.amount = 'Enter a payable amount or select an outstanding debt to adjust.';
		}

		if (Object.keys(newErrors).length) {
			setErrors(newErrors);
			return;
		}

		setErrors({});
		const updatedData = {
			paid_date: searchstartDate,
			payment_type: paymentType,
			utr: paymentType === 'Bank Transaction' ? utr : '',
			remark: remark || '',
			amount: Number(payableAmount) || 0,
			va_ids: selectedIds,
			roundedOffDifference: roundedOffDifference,
		};

		handleSubmit(updatedData);
		onClose();
	};

	// --- Field-specific error clearing ---
	const handleUtrChange = (e) => {
		setUtr(e.target.value);
		if (errors.utr) setErrors((prev) => ({ ...prev, utr: '' }));
	};

	const handlePaymentTypeChange = (e) => {
		setPaymentType(e.target.value);
		if (errors.type) setErrors((prev) => ({ ...prev, type: '' }));
	};

	const handleRemarkChange = (e) => {
		setRemark(e.target.value);
	};

	const handleDateChange = (date) => {
		setSearchStartDate(date);
		if (errors.date) setErrors((prev) => ({ ...prev, date: '' }));
	};

	return (
		<Modal
			className="relative p-5 bg-white dark:bg-gray-700 dark:text-white rounded-lg"
			style={{ width: '500px',height: 'auto', maxHeight: '80vh', overflowY: 'auto' }}
			isOpen={isOpen}
			onClose={onClose}
		>
			<ModalBody className="custom-modal" style={{ margin: '0' }}>
				<h2 className="text-center font-bold text-lg">{title}</h2>
				{outstandingData && outstandingData.length > 0 ? (
					<div className="overflow-x-auto mt-4">
						<table className="table-auto w-full border-collapse border border-gray-300">
							<thead>
								<tr className="bg-gray-200">
									<th className="border border-gray-300 px-4 py-2">Choose</th>
									<th className="border border-gray-300 px-4 py-2">
										Reference No
									</th>
									<th className="border border-gray-300 px-4 py-2">
										Outstanding Amount
									</th>
									<th className="border border-gray-300 px-4 py-2">Reason</th>
								</tr>
							</thead>
							<tbody>
								{outstandingData.map((item, index) => (
									<tr key={index} className="hover:bg-gray-100">
										<td className="border border-gray-300 px-4 py-2 text-center">
											<input
												type="checkbox"
												autoComplete="off"
												checked={selectedIds.includes(item.id)}
												onChange={(e) =>
													handleCheckboxChange(
														item.outstanding_amount,
														item.id,
														e.target.checked
													)
												}
												disabled={
													isSelectionDisabled &&
													!selectedIds.includes(item.id)
												}
											/>
										</td>
										<td className="border border-gray-300 px-4 py-2">
											{item.ref_no || 'N/A'}
										</td>
										<td className="border border-gray-300 px-4 py-2">
											{item.outstanding_amount || 'N/A'}
										</td>
										<td className="border border-gray-300 px-4 py-2">
											{item.reason || 'N/A'}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<p className="text-gray-500 mt-4"></p>
				)}
				{isNegativeBill ? (
					<p className="text-red-600 mt-4">
						Payable amount is negative (₹{requestAmount}) — the vendor
						appears to owe money back on this bill. This can't be
						saved as a payment; resolve it as outstanding recovery
						first.
					</p>
				) : (
					isSelectionDisabled && (
						<p className="text-green-600 mt-4">
							Fully covered by adjustment — ready to save. Uncheck items to use less.
						</p>
					)
				)}

				<div className="mt-4 flex space-x-4">
					<div className="flex-1">
						<label className="block text-gray-700">Bill Amount:</label>
						<input
							type="text"
							value={requestAmount}
							readOnly
							className="input w-full border border-gray-300 rounded-md p-2 mt-2"
						/>
					</div>

					{outstandingData && outstandingData.length > 0 && (
						<div className="flex-1">
							<label className="block text-gray-700">Adjustable Amount:</label>
							<input
								type="text"
								value={totalAdjusted}
								readOnly
								className="input w-full border border-gray-300 rounded-md p-2 mt-2"
							/>
						</div>
					)}

					<div className="flex-1">
						<label className="block text-gray-700">Payable Amount:</label>
						{/* Always the full remaining amount — partial payment isn't
						    allowed here, so this is display-only, not editable. */}
						<input
							type="number"
							value={payableAmount}
							readOnly
							className="input w-full border border-gray-300 rounded-md p-2 mt-2 bg-gray-100"
						/>
						{errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
					</div>
				</div>

				{isAutoAdjusted && (
					<p className="text-green-600 text-sm mt-2">
						Fully covered by selected debt — no cash payment needed.
					</p>
				)}

				<div
					className="max-h-96 flex flex-col gap-5 mt-4"
					style={{ maxHeight: '60vh', scrollbarWidth: 'thin' }}
				>
					<div className="flex flex-col flex-1">
						<LabelArea label="Payment Date" />
						<InputSearchDate
							value={searchstartDate}
							onChange={handleDateChange}
						/>
						{errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
					</div>

					{!noCashNeeded && (
						<div className="flex flex-col flex-1">
							<LabelArea label="Payment Type" />
							<select
								className="bg-white border border-gray-200 h-10 text-sm block w-full focus:bg-white focus:border-gray focus:ring-1 focus:ring-blue-500 rounded"
								value={paymentType || ''}
								onChange={handlePaymentTypeChange}
							>
								<option value="">Choose</option>
								<option value="Bank Transaction">Net Banking</option>
								<option value="Cash">Cash</option>
							</select>
							{errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
						</div>
					)}

					{paymentType === 'Bank Transaction' && (
						<Input
							className={`border h-10 text-sm block w-full ${
								errors.utr ? 'border-red-500' : ''
							} focus:bg-white`}
							type="text"
							placeholder="Enter UTR or Reference ID"
							value={utr}
							onChange={handleUtrChange}
						/>
					)}

					{paymentType && (
						<div className="flex flex-col flex-1">
							<LabelArea label="Remark" />
							<Input
								className="border h-10 text-sm block w-full focus:bg-white"
								type="text"
								placeholder="Enter remark"
								value={remark}
								onChange={handleRemarkChange}
							/>
						</div>
					)}

					<div className="flex justify-around mt-3">
						<button
							onClick={onClose}
							className="btn btn-secondary px-4 py-2 bg-gray-300 text-gray-700 rounded-md"
						>
							Cancel
						</button>
						<button
							onClick={onSubmit}
							className="btn btn-primary px-4 py-2 bg-green-500 text-white rounded-md"
						>
							Save
						</button>
					</div>
				</div>
			</ModalBody>
		</Modal>
	);
};

export default BillDetailsPaymentModal;

